package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import dev.lingvo.shared.screenshots.design.derive.DeriveDevicesRequest
import dev.lingvo.shared.screenshots.design.derive.DeriveDevicesResponse
import dev.lingvo.shared.screenshots.design.derive.fitDerivedDevice
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.render.RenderInputs
import dev.lingvo.shared.screenshots.render.RenderMode
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.ApiErrorBody
import dev.lingvo.site.api.ApiConfig
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.studio.preview.DesignLooksPort
import dev.lingvo.site.components.studio.preview.PreviewRenderer
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import kotlinx.serialization.json.Json
import org.w3c.dom.HTMLCanvasElement
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

internal data class ResizeFitCheck(val accepted: Boolean, val message: String)

/** Checks all text, including literals and overrides, rather than the thumbnail's first-caption summary. */
@Composable
internal fun ResizeFit(original: ScreenshotDesign, data: DesignPageData, device: String, looks: DesignLooksPort, onCheck: (ResizeFitCheck) -> Unit) {
    val renderer = remember(looks) { PreviewRenderer(looks) }
    LaunchedEffect(original, data.captions, device, looks) {
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        try {
            val board = original.boards.first { it.deviceClass == device }
            val ordered = board.pages.flatMap { it.slotIds }
            for (locale in (listOf(data.project.sourceLocale) + data.project.locales).distinct()) {
                val captions: (TextContent, Int) -> String? = { content, index -> when (content) {
                    is TextContent.Literal -> content.text
                    is TextContent.Key -> captionValue(data, locale, content.key)
                    is TextContent.SlotCaption -> ordered.getOrNull(index)?.let { captionValue(data, locale, it) }
                } }
                renderer.prepare(original, board.id, locale, captions)
                val fits = fitDerivedDevice(original, device, RenderInputs(locale, RenderMode.EDITOR, captions, { _, _ -> null },
                    looks.measurerFor(canvas), looks.segmenter, looks.fonts, looks.scripts,
                    looks.templates().firstOrNull { it.id == original.templateId }?.families.orEmpty()))
                if (fits.any { !it.accepted }) {
                    onCheck(ResizeFitCheck(false, trf("screenshots.resize.floor_refused", "Text does not fit at its minimum size or is missing in {locale}. Edit the source or this device.", "locale" to locale)))
                    return@LaunchedEffect
                }
            }
            onCheck(ResizeFitCheck(true, ""))
        } catch (cancelled: CancellationException) { throw cancelled }
        catch (_: Throwable) { onCheck(ResizeFitCheck(false, tr("app.projects.slug.screenshots.design_resize_fit.fonts_could_not_load_close_and", "Fonts could not load. Close and reopen the preview to retry."))) }
        finally { canvas.width = 0; canvas.height = 0 }
    }
}

/** This route-local transport keeps :kit unchanged and sends no credentials outside the configured API. */
internal suspend fun deriveDesignDevices(slug: String, platform: StorePlatform, request: DeriveDevicesRequest): DeriveDevicesResponse {
    val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }
    val headers = Headers().apply {
        set("Accept", "application/json")
        set("Content-Type", "application/json")
        Session.token?.let { set("Authorization", "Bearer $it") }
    }
    val abort: dynamic = js("typeof AbortController === 'undefined' ? null : new AbortController()")
    val init = RequestInit(method = "POST", headers = headers, body = json.encodeToString(DeriveDevicesRequest.serializer(), request))
    if (abort != null) init.asDynamic().signal = abort.signal
    try {
        val response = window.fetch("${ApiConfig.baseUrl}/projects/${encodeResizeSegment(slug)}/screenshots/${platform.id}/design/derive", init).await()
        val raw = response.text().await()
        if (!response.ok) {
            val error = runCatching { json.decodeFromString(ApiErrorBody.serializer(), raw).error }.getOrNull()
            throw ApiException(response.status.toInt(), error?.code ?: "derive_failed",
                tr("app.projects.slug.screenshots.design_resize_fit.the_devices_could_not_be_derived", "The devices could not be derived. Retry after checking your connection."))
        }
        return json.decodeFromString(DeriveDevicesResponse.serializer(), raw)
    } catch (cancelled: CancellationException) {
        abort?.abort()
        throw cancelled
    }
}

private fun encodeResizeSegment(value: String): String = js("encodeURIComponent")(value) as String
